// components/LifeGoalsForm.js
import React, { useState } from 'react';
import { 
  CardContent, 
  Typography, 
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Box,
  Grid,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { financialService } from '../utils/api';
import { useAppContext } from '../context/AppContext';
import CONFIG from '../utils/config';

const EMPTY_GOAL = { type: '', currentValue: 0, targetYear: 0 };

function LifeGoalsForm({ formData, onAddGoal, onRemoveGoal }) {
  const { state } = useAppContext();
  const [open,        setOpen]        = useState(false);
  const [newGoal,     setNewGoal]     = useState(EMPTY_GOAL);
  const [isSaving,    setIsSaving]    = useState(false);
  const [saveError,   setSaveError]   = useState('');
  const currentYear = new Date().getFullYear();

  const handleOpen  = () => { setSaveError(''); setOpen(true); };
  const handleClose = () => { setOpen(false); setNewGoal(EMPTY_GOAL); setSaveError(''); };

  const handleChange = (field, value) => {
    setNewGoal((prev) => ({ ...prev, [field]: value }));
  };

  const parseNumber = (e) => {
    const v = parseInt(e.target.value.replace(/,/g, ''), 10);
    return isNaN(v) ? 0 : v;
  };

  const formatNumberINR = (num) => new Intl.NumberFormat('en-IN').format(num);

  const formatNumber = (amount) => {
    if (amount >= 10000000) return (amount / 10000000).toFixed(2) + ' Crores';
    if (amount >= 100000)   return (amount / 100000).toFixed(2) + ' Lakhs';
    return new Intl.NumberFormat('en-IN').format(amount);
  };

  const handleAddGoal = async () => {
    setSaveError('');
    const yearsToGoal = newGoal.targetYear - currentYear;

    if (yearsToGoal < 0) {
      setSaveError('Target year must be in the future.');
      return;
    }

    const futureValue = Math.round(
      newGoal.currentValue * Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, yearsToGoal)
    );

    setIsSaving(true);
    try {
      const response = await financialService.submitGoals(state.phoneNumber, {
        goal_type:    newGoal.type,
        current_value:newGoal.currentValue,
        target_date:  newGoal.targetYear,
        future_value: futureValue,
      });

      // Handle both { id } and { data: { id } } response shapes
      const savedId = response?.id ?? response?.data?.id ?? null;

      onAddGoal({
        type:         newGoal.type,
        currentValue: newGoal.currentValue,
        targetYear:   newGoal.targetYear,
        FV_goal:      futureValue,
        id:           savedId,
      });

      handleClose();
    } catch (err) {
      console.error('Goal save failed:', err);
      setSaveError('Failed to save goal. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveGoal = async (goal, index) => {
    if (!window.confirm('Are you sure you want to remove this goal?')) return;
    try {
      await financialService.deleteGoal(state.phoneNumber, goal.id);
      onRemoveGoal(index);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const isFormValid =
    newGoal.type.trim() &&
    newGoal.currentValue > 0 &&
    newGoal.targetYear >= currentYear;

  return (
    <CardContent>
      <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }} gutterBottom>
        Other Life Goals
      </Typography>
      <Typography variant="body1" gutterBottom>
        What other life goals would you like to plan for?
      </Typography>

      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{ mb: 2 }}
      >
        Add Goal
      </Button>

      {/* Goals List */}
      <List>
        {formData.goals?.length > 0 ? (
          formData.goals.map((goal, index) => (
            <ListItem key={goal.id ?? index} divider>
              <ListItemText
                primary={goal.type}
                secondary={`₹${formatNumber(goal.FV_goal)} required in ${goal.targetYear} (Current value: ₹${formatNumber(goal.currentValue)})`}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleRemoveGoal(goal, index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 2 }}>
            Think of any large value purchases: home, car; large value expenses: children's
            marriage; value experiences: bucket list travel; or any planned gifts and legacies.
          </Typography>
        )}
      </List>

      {/* Add Goal Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Goal</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={1}>
              <TextField
                label="Goal Name"
                value={newGoal.type}
                onChange={(e) => handleChange('type', e.target.value)}
                fullWidth
                margin="dense"
                helperText='e.g., Dream Home, Wedding'
                required
              />
              <TextField
                label="Current Value"
                value={newGoal.currentValue ? formatNumberINR(newGoal.currentValue) : ''}
                onChange={(e) => handleChange('currentValue', parseNumber(e))}
                type="tel"
                inputMode="numeric"
                fullWidth
                margin="dense"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Current cost of your goal in today's money"
                required
              />
              <TextField
                label="Target Year"
                value={newGoal.targetYear || ''}
                onChange={(e) => handleChange('targetYear', Number(e.target.value))}
                type="tel"
                inputMode="numeric"
                fullWidth
                margin="dense"
                helperText="When do you plan to achieve this goal?"
                inputProps={{ min: currentYear, max: currentYear + 50 }}
                required
              />
            </Grid>

            {saveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGoal}
            color="primary"
            variant="contained"
            disabled={!isFormValid || isSaving}
          >
            {isSaving ? 'Saving…' : 'Add Goal'}
          </Button>
        </DialogActions>
      </Dialog>
    </CardContent>
  );
}

export default LifeGoalsForm;
